import React, { useState, useEffect, useRef } from "react";
import teamService from "../../services/api/teamService";
import { getUserSports, getAllSports } from "../../services/api/sportsService";
import { searchByUsername } from "../../services/api/userService";
import GameLayout from "../layout/GameLayout";
import { Input } from "../ui/Input";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import VenueSearch from "../ui/VenueSearch";
import { sportOnCourt } from "../../utils/teamCapacity";
import "../../styles/features/teams.css";

const TeamCreator = ({ currentUser, token, onLogout, onTeamCreated, onCancel }) => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const createBtnRef = React.useRef(null);

  const [teamData, setTeamData] = useState({
    name: "",
    description: "",
    sport_id: "",
    organization: "",
    location: "",
    venue_address: "",
    venue_lat: null,
    venue_lng: null,
    venue_place_id: "",
    logo_url: "",
    is_public: true,
    skill_level: "",
    requires_approval: true,
    max_members: 2,
    rules: {
      substitutions: 1,
      age_restriction: "none",
    },
  });

  const [inviteQueue, setInviteQueue] = useState([]);
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const inviteDebounce = useRef(null);

  const fetchSports = async () => {
    try {
      // Load user's own sports first; fall back to all active sports if none
      let sportsData = [];
      try {
        const userRes = await getUserSports();
        sportsData = userRes.data || userRes.sports || [];
      } catch {
        sportsData = [];
      }
      if (!sportsData.length) {
        const allRes = await getAllSports();
        sportsData = allRes.data || [];
      }
      setSports(sportsData);
    } catch (err) {
      console.error("Error fetching sports:", err);
      setError("Failed to load sports");
    }
  };

  useEffect(() => {
    fetchSports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field, value) => {
    if (field === "max_members") {
      startersEditedRef.current = true;
    }
    setTeamData((prev) => ({ ...prev, [field]: value }));
  };

  // Starter slots default to the sport's team size (basketball 5, volleyball
  // 6, football 11, badminton/TT 2) until the user types their own (TM-B19).
  const startersEditedRef = useRef(false);
  const handleSportChange = (sportId) => {
    const sport = sports.find((s) => Number(s.id) === Number(sportId));
    // sports.team_size is often empty: fall back to the sport's on-court size
    const teamSize = parseInt(sport?.team_size, 10) || sportOnCourt(sport?.name);
    setTeamData((prev) => ({
      ...prev,
      sport_id: sportId,
      ...(!startersEditedRef.current && teamSize >= 2 ? { max_members: teamSize } : {}),
    }));
  };

  const handleRulesChange = (field, value) => {
    setTeamData((prev) => ({ ...prev, rules: { ...prev.rules, [field]: value } }));
  };

  const handleAddInvite = (user) => {
    // Never invite yourself — creator auto-joins on team creation
    if (currentUser && String(user.id) === String(currentUser.id)) return;
    if (inviteQueue.some((u) => u.userId === user.id)) return;
    // Total capacity = starters + substitute slots. Creator auto-joins, so -1.
    const maxStarters   = parseInt(teamData.max_members)         || 2;
    const maxSubs       = parseInt(teamData.rules.substitutions) || 0;
    const totalCapacity = maxStarters + maxSubs - 1;
    if (inviteQueue.length >= totalCapacity) return; // silently block; UI shows count
    setInviteQueue((prev) => [
      ...prev,
      { userId: user.id, username: user.username, firstName: user.first_name, lastName: user.last_name, avatar_url: user.avatar_url },
    ]);
    setInviteSearch("");
    setInviteResults([]);
  };

  const handleRemoveInvite = (userId) => {
    setInviteQueue((prev) => prev.filter((u) => u.userId !== userId));
  };

  const handleInviteSearchChange = (e) => {
    const q = e.target.value;
    setInviteSearch(q);
    clearTimeout(inviteDebounce.current);
    if (q.length < 2) { setInviteResults([]); return; }
    inviteDebounce.current = setTimeout(async () => {
      try {
        setInviteSearching(true);
        const res = await searchByUsername(q);
        const all = res.data?.users || [];
        // Never show the creator themselves in invite results — they auto-join
        setInviteResults(all.filter(u => String(u.id) !== String(currentUser?.id)));
      } catch {
        setInviteResults([]);
      } finally {
        setInviteSearching(false);
      }
    }, 300);
  };

  const handleSubmit = async () => {
    if (!teamData.name.trim()) { setError("Team name is required"); setTimeout(() => createBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); return; }
    if (!teamData.location || !teamData.location.trim()) { setError("Team location is required — help players find your home venue"); setTimeout(() => createBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); return; }
    if (!teamData.sport_id) { setError("Please select a sport"); setTimeout(() => createBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); return; }
    if (teamData.max_members < 2) { setError("Team must allow at least 2 members"); setTimeout(() => createBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); return; }

    try {
      setLoading(true);
      setError("");
      const result = await teamService.createTeam(teamData);
      // Backend wraps team in { success, data, message } — unwrap it
      const newTeam = result.data || result;

      if (inviteQueue.length > 0) {
        await Promise.allSettled(
          inviteQueue.map((user) =>
            teamService.inviteUserToTeam(newTeam.id, { userId: user.userId, requestedRole: "player" })
          )
        );
      }

      if (onTeamCreated) onTeamCreated(newTeam);
    } catch (err) {
      console.error("Error creating team:", err);
      setError(err.message || "Failed to create team. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "6px",
    background: "rgba(255,255,255,0.05)",
    color: "white",
    fontSize: "0.9rem",
    boxSizing: "border-box",
  };

  const labelStyle = {
    color: "rgba(255,255,255,0.9)",
    fontWeight: "500",
    fontSize: "0.82rem",
    marginBottom: "0.4rem",
    display: "block",
  };

  return (
    <GameLayout token={token} onLogout={onLogout} profile={currentUser} showFloatingNav={true}>
      <div className="team-management-gaming">
        {/* Header */}
        <div className="teams-header-gaming">
          <FloatingCard className="header-card-gaming delay-1">
            <div className="teams-nav-gaming">
              <div className="teams-nav-left">
                <h2 className="teams-title-gaming">🏆 Create New Team</h2>
                <p className="teams-subtitle-gaming">Fill in the details below and hit Create Team</p>
              </div>
              <div className="teams-nav-right">
                <FloatingButton variant="outline" size="medium" onClick={onCancel} icon="←">
                  <span className="btn-text">Back</span>
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>

        {error && (
          <FloatingCard className="delay-2" style={{ marginBottom: "1rem", backgroundColor: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)" }}>
            <div className="error-alert-gaming" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 }}>
                <span className="alert-icon">⚠️</span>
                <span className="alert-text">{error}</span>
              </div>
              <button
                onClick={() => setError("")}
                aria-label="Dismiss error"
                style={{ background: "none", border: "1px solid rgba(220,38,38,0.4)", borderRadius: "6px", color: "rgba(255,255,255,0.7)", cursor: "pointer", padding: "0.2rem 0.5rem", fontSize: "0.8rem", flexShrink: 0, lineHeight: 1 }}
              >×</button>
            </div>
          </FloatingCard>
        )}

        {/* Single page — 3 columns */}
        <div className="team-creator-cols"  style={{ gap: "1rem", alignItems: "start" }}>

          {/* ── Column 1: Team Information ─────────────────────────────── */}
          <FloatingCard className="delay-3">
            <div className="card-header-gaming">
              <h3 className="card-title-gaming"><span className="title-icon">📋</span> Team Information</h3>
            </div>
            <div className="card-content-gaming" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

              <div>
                <label style={labelStyle}>Team Name *</label>
                <Input
                  type="text"
                  value={teamData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Thunder FC"
                  style={inputStyle}
                />
              </div>

              {/* Sport Dropdown */}
              <div>
                <label style={labelStyle}>Sport *</label>
                <select
                  value={teamData.sport_id}
                  onChange={(e) => handleSportChange(e.target.value ? Number(e.target.value) : "")}
                  style={{
                    ...inputStyle,
                    background: "rgba(10,14,26,0.95)",
                    cursor: "pointer",
                    appearance: "auto",
                  }}
                >
                  <option value="">— Select a sport —</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.icon ? `${s.icon} ` : ""}{s.name}
                    </option>
                  ))}
                </select>
                {sports.length === 0 && (
                  <p style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", margin: "0.3rem 0 0" }}>
                    Loading sports…
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle}>Organization</label>
                <Input
                  type="text"
                  value={teamData.organization}
                  onChange={(e) => handleInputChange("organization", e.target.value)}
                  placeholder="e.g., City Sports Club"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Location <span style={{ color: "#f87171" }}>*</span></label>
                <VenueSearch
                  label=""
                  hideCourt={true}
                  value={{
                    venue: teamData.location,
                    venue_place_id: teamData.venue_place_id,
                    venue_address: teamData.venue_address,
                    venue_lat: teamData.venue_lat,
                    venue_lng: teamData.venue_lng,
                    court: "",
                  }}
                  onChange={(v) => {
                    handleInputChange("location", v.venue);
                    handleInputChange("venue_address", v.venue_address);
                    handleInputChange("venue_lat", v.venue_lat);
                    handleInputChange("venue_lng", v.venue_lng);
                    handleInputChange("venue_place_id", v.venue_place_id);
                  }}
                />
              </div>

              <div>
                <label style={labelStyle}>Team Description</label>
                <textarea
                  value={teamData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Tell people about your team…"
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                />
              </div>
            </div>
          </FloatingCard>

          {/* ── Column 2: Team Settings ──────────────────────────────────── */}
          <FloatingCard className="delay-4">
            <div className="card-header-gaming">
              <h3 className="card-title-gaming"><span className="title-icon">⚙️</span> Team Settings</h3>
            </div>
            <div className="card-content-gaming" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

              <div>
                <label style={labelStyle}>🌐 Privacy</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                  {[{ value: true, label: "Public Team" }, { value: false, label: "Private Team" }].map(({ value, label }) => (
                    <label key={String(value)} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", color: "white"}}>
                      <input type="radio" name="visibility" checked={teamData.is_public === value} onChange={() => handleInputChange("is_public", value)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>🏅 Skill Level</label>
                <select
                  value={teamData.skill_level || ""}
                  onChange={e => handleInputChange("skill_level", e.target.value || null)}
                  style={{ width: "100%", padding: "0.6rem", borderRadius: "8px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontSize: "0.9rem" }}
                >
                  <option value="">Any skill level</option>
                  <option value="beginner">🌿 Beginner</option>
                  <option value="recreational">🌱 Recreational</option>
                  <option value="intermediate">⚡ Intermediate</option>
                  <option value="advanced">🔥 Advanced</option>
                  <option value="professional">👑 Professional</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>✋ Join Approval</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                  {[{ value: true, label: "Manual Approval" }, { value: false, label: "Auto Join" }].map(({ value, label }) => (
                    <label key={String(value)} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", color: "white"}}>
                      <input type="radio" name="approval" checked={teamData.requires_approval === value} onChange={() => handleInputChange("requires_approval", value)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>👥 Maximum Members</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={teamData.max_members === '' ? '' : teamData.max_members}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    if (raw === '') {
                      handleInputChange('max_members', '');
                    } else {
                      const v = parseInt(raw, 10);
                      handleInputChange('max_members', Math.min(999, v));
                    }
                  }}
                  onBlur={() => {
                    const v = parseInt(teamData.max_members, 10);
                    handleInputChange('max_members', Number.isNaN(v) || v < 2 ? 2 : Math.min(v, 999));
                  }}
                  placeholder="2 – 999"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>🔄 Substitutions Allowed</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={teamData.rules.substitutions === '' ? '' : (Number.isFinite(teamData.rules.substitutions) ? teamData.rules.substitutions : 0)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    if (raw === '') {
                      handleRulesChange('substitutions', '');
                    } else {
                      const v = parseInt(raw, 10);
                      handleRulesChange('substitutions', Math.min(99, v));
                    }
                  }}
                  onBlur={() => {
                    const v = parseInt(teamData.rules.substitutions, 10);
                    handleRulesChange('substitutions', Number.isNaN(v) || v < 0 ? 0 : Math.min(v, 99));
                  }}
                  placeholder="0 – 99"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>🔞 Age Restriction</label>
                <select
                  value={teamData.rules.age_restriction}
                  onChange={(e) => handleRulesChange("age_restriction", e.target.value)}
                  style={{ ...inputStyle, background: "rgba(10,14,26,0.95)", cursor: "pointer", appearance: "auto" }}
                >
                  <option value="none">No Restriction</option>
                  <option value="18+">18+ Only</option>
                  <option value="21+">21+ Only</option>
                  <option value="under18">Under 18</option>
                </select>
              </div>
            </div>
          </FloatingCard>

          {/* ── Column 3: Invite Members ─────────────────────────────────── */}
          <FloatingCard className="delay-5" style={{ display: "flex", flexDirection: "column" }}>
            <div className="card-header-gaming">
              <h3 className="card-title-gaming"><span className="title-icon">📧</span> Invite Members <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", fontWeight: 400 }}>(Optional)</span></h3>
            </div>
            <div className="card-content-gaming" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.6)", marginBottom: "0.75rem", flexShrink: 0 }}>
                Search by username to invite players
              </p>

              {/* Search */}
              <div style={{ position: "relative", marginBottom: "0.75rem", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", padding: "0.5rem 0.75rem" }}>
                  <span>🔍</span>
                  <input
                    type="text"
                    value={inviteSearch}
                    onChange={handleInviteSearchChange}
                    placeholder="Search players by username…"
                    autoComplete="off"
                    style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "white", fontSize: "0.9rem" }}
                  />
                  {inviteSearching && <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)" }}>…</span>}
                </div>

                {inviteResults.length > 0 && (
                  <ul style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "rgba(10,14,26,0.97)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", marginTop: "4px", padding: 0, listStyle: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
                    {inviteResults.map((user) => (
                      <li
                        key={user.id}
                        onClick={() => handleAddInvite(user)}
                        style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.6rem 0.9rem", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.06)", transition: "background 0.15s" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,255,136,0.08)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        {user.avatar_url
                          ? <img src={user.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                          : <span style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(0,255,136,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem" }}>👤</span>
                        }
                        <div>
                          <div style={{ fontSize: "0.9rem", color: "white", fontWeight: "500" }}>{user.first_name} {user.last_name}</div>
                          <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>@{user.username}</div>
                        </div>
                        {inviteQueue.some((u) => u.userId === user.id) && (
                          <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#00ff88" }}>✓ Added</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {inviteSearch.length >= 2 && !inviteSearching && inviteResults.length === 0 && (
                  <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.45)", padding: "0.5rem 0.75rem" }}>No players found</div>
                )}
              </div>

              {/* Queue — scrollable, max 300 px tall so the card never pushes the Create button off-screen */}
              {inviteQueue.length > 0 && (
                <div style={{
                  overflowY: "auto",
                  maxHeight: "300px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  marginBottom: "0.75rem",
                  paddingRight: "2px", /* prevent scrollbar from clipping border */
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(0,255,136,0.3) transparent",
                }}>
                  {inviteQueue.map((user) => (
                    <div key={user.userId} style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(0,255,136,0.06)", border: "1px solid rgba(0,255,136,0.15)", borderRadius: "6px", padding: "0.5rem 0.75rem", flexShrink: 0 }}>
                      {user.avatar_url
                        ? <img src={user.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                        : <span style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(0,255,136,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", flexShrink: 0 }}>👤</span>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "0.9rem", color: "white", fontWeight: "500" }}>{user.firstName} {user.lastName}</div>
                        <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>@{user.username}</div>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "rgba(0,255,136,0.7)", marginRight: "0.5rem" }}>Player</span>
                      <FloatingButton variant="danger" size="small" onClick={() => handleRemoveInvite(user.userId)} icon="🗑️" />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ padding: "0.65rem 0.75rem", background: "rgba(0,255,136,0.08)", borderRadius: "6px", border: "1px solid rgba(0,255,136,0.2)", fontSize: "0.82rem", color: "white", flexShrink: 0 }}>
                {(() => {
                  const maxStarters   = parseInt(teamData.max_members)         || 2;
                  const maxSubs       = parseInt(teamData.rules.substitutions) || 0;
                  // Creator auto-joins as a starter, so available invite slots = starters + subs - 1
                  const totalCapacity = maxStarters + maxSubs - 1;
                  const remaining     = Math.max(0, totalCapacity - inviteQueue.length);
                  return <>
                    📊 <strong data-testid="invite-queue-count">{`${inviteQueue.length}/${totalCapacity}`}</strong>{` player${totalCapacity === 1 ? "" : "s"} queued`}
                    <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", marginLeft: "0.4rem" }}>({maxStarters} starters + {maxSubs} subs − 1 creator)</span>
                    {inviteQueue.length > 0 && <span style={{ marginLeft: "0.4rem" }}>— they'll receive an invite after the team is created</span>}
                    {remaining === 0 && <span style={{ color: "#ffb000", marginLeft: "0.4rem" }}>⚠ Team capacity reached</span>}
                  </>;
                })()}
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Create Team Button */}
        <div ref={createBtnRef}>
        <FloatingCard className="delay-6" style={{ marginTop: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
            <FloatingButton variant="outline" size="medium" onClick={onCancel} icon="❌">
              <span className="btn-text">Cancel</span>
            </FloatingButton>
            <FloatingButton variant="primary" size="medium" onClick={handleSubmit} disabled={loading} icon={loading ? "⏳" : "🏆"}>
              <span className="btn-text">{loading ? "Creating Team…" : "Create Team"}</span>
            </FloatingButton>
          </div>
        </FloatingCard>
        </div>
      </div>
    </GameLayout>
  );
};

export default TeamCreator;
